import { useLocation, useNavigate, type Location, type To } from 'react-router';

export type AppState =
  | {
      /**
       * Where to return after the destination page's save/delete flow. Carries its own state so
       * return navigations can restore it: react-router drops `to.state` when navigating to a
       * stored Location, so the chain must be passed back explicitly (see useReturnNavigation).
       */
      from?: Location<AppState>;
      /** Lets one tagged navigation pass the unsaved-changes blocker; see BlockNavigationDialog */
      skipUnsavedChangesPrompt?: true;
      /** Set once, by the Google Workspace callback, so the provider page can confirm the connection */
      googleIdPConnected?: true;
    }
  | undefined;

type LocationWithState = Location<AppState>;

export const useAppLocation = useLocation as () => LocationWithState;

/**
 * The state to attach when navigating back to a stored `from` Location, so the origin page's own
 * return navigation keeps working. Only the return chain is copied: one-shot flags like
 * `skipUnsavedChangesPrompt` must not re-arm on a fresh navigation (see BlockNavigationDialog),
 * so never re-attach `from.state` wholesale.
 */
export function returnState(from: LocationWithState | undefined): AppState {
  return from?.state?.from ? { from: from.state.from } : undefined;
}

/**
 * Navigation for the save/delete exits of pages entered with `state.from`: goes back to `from`
 * when present, else to `fallbackTo`, re-attaching the return chain via {@link returnState}.
 * Replaces by default so backing out of the destination doesn't land in the form that was just
 * submitted; `state` merges extra one-shot flags (e.g. SKIP_UNSAVED_CHANGES_PROMPT after a
 * delete).
 */
export function useReturnNavigation(fallbackTo: To) {
  const navigate = useNavigate();
  const from = useAppLocation().state?.from;

  return (options?: { replace?: boolean; state?: AppState }) => {
    void navigate(from ?? fallbackTo, {
      replace: options?.replace ?? true,
      state: options?.state ? { ...returnState(from), ...options.state } : returnState(from),
    });
  };
}
